import { resolveEnvKey, type EnvKey } from './env-profiles';

const CANONICAL_VAULT_SUPERADMIN_BY_ENV: Record<EnvKey, string> = {
  dev: 'dev@datavault.com',
  qa: 'qa@datavault.com',
  prod: 'admin@datavault.com',
};

export const SIMAPPE_REPLICATION_ADMIN_EMAIL = 'dev@datavault.com';

export function isSimappeReplicationAdminEmail(email: string | undefined | null): boolean {
  return (
    normalizeVaultSuperadminEmail(email) ===
    normalizeVaultSuperadminEmail(SIMAPPE_REPLICATION_ADMIN_EMAIL)
  );
}

/** Ocultar dev@datavault.com en listados UI fuera del entorno dev (no bloquea login). */
export function isSimappeReplicationAdminBlockedInCurrentEnv(
  email: string | undefined | null,
): boolean {
  return resolveEnvKey() !== 'dev' && isSimappeReplicationAdminEmail(email);
}

export function resolveCanonicalVaultSuperadminEmail(): string {
  return CANONICAL_VAULT_SUPERADMIN_BY_ENV[resolveEnvKey()];
}

export function normalizeVaultSuperadminEmail(email: string | undefined | null): string {
  return (email ?? '').trim().toLowerCase();
}


export function vaultSessionEmailBase(email: string): string {
  const normalized = normalizeVaultSuperadminEmail(email);
  const at = normalized.indexOf('@');
  if (at < 0) return normalized;
  const local = normalized.slice(0, at);
  const domain = normalized.slice(at + 1).replace(/\.[a-z0-9]+$/i, '');
  return `${local}@${domain}`;
}

export function vaultSessionIdentitiesMatch(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const left = normalizeVaultSuperadminEmail(a);
  const right = normalizeVaultSuperadminEmail(b);
  if (!left || !right) return false;
  if (left === right) return true;
  return vaultSessionEmailBase(left) === vaultSessionEmailBase(right);
}

function vaultSuperadminEmailBase(email: string): string {
  return vaultSessionEmailBase(email);
}

export function emailIsCanonicalVaultSuperadmin(email: string | undefined | null): boolean {
  const candidate = normalizeVaultSuperadminEmail(email);
  if (!candidate) return false;


  if (isSimappeReplicationAdminEmail(candidate)) return true;

  const canonical = normalizeVaultSuperadminEmail(resolveCanonicalVaultSuperadminEmail());
  if (!canonical) return false;
  if (candidate === canonical) return true;
  return vaultSuperadminEmailBase(candidate) === vaultSuperadminEmailBase(canonical);
}


export function emailIsVaultSystemSuperadmin(email: string | undefined | null): boolean {
  const candidate = normalizeVaultSuperadminEmail(email);
  if (!candidate) return false;
  if (isSimappeReplicationAdminEmail(candidate)) return true;

  const candidateBase = vaultSuperadminEmailBase(candidate);
  return Object.values(CANONICAL_VAULT_SUPERADMIN_BY_ENV).some((canonical) => {
    const norm = normalizeVaultSuperadminEmail(canonical);
    return candidate === norm || candidateBase === vaultSuperadminEmailBase(norm);
  });
}


export function readSessionUserEmail(): string {
  if (typeof localStorage === 'undefined') return '';
  try {
    const raw = localStorage.getItem('user_data');
    if (raw) {
      const user = JSON.parse(raw) as Record<string, unknown>;
      const email = String(user['email'] ?? user['username'] ?? '').trim();
      if (email) return email;
    }
  } catch {
    /* ignore */
  }

  const token = localStorage.getItem('access_token');
  if (!token) return '';
  try {
    const part = token.split('.')[1];
    if (!part) return '';
    const padded = part.replace(/-/g, '+').replace(/_/g, '/');
    const pad = padded + '='.repeat((4 - (padded.length % 4)) % 4);
    const payload = JSON.parse(atob(pad)) as Record<string, unknown>;
    const user = (payload['User'] ?? payload['user']) as Record<string, unknown> | undefined;
    return String(user?.['email'] ?? user?.['username'] ?? payload['email'] ?? '').trim();
  } catch {
    return '';
  }
}

export function sessionUserIsCanonicalVaultSuperadmin(): boolean {
  return emailIsCanonicalVaultSuperadmin(readSessionUserEmail());
}
